import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { SPRING_PILL } from "@/lib/motion";
import { Loader2, Search, SlidersHorizontal, Sparkles, Tag, Utensils, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/Button";
import { useTranslation } from "@/i18n/I18nProvider";
import { useTaxonomy } from "@/data/TaxonomyProvider";
import { labelFor } from "@/lib/taxonomyLabels";
import {
  hasToken,
  INTENT_KEYS,
  type ChipOption,
  type FilterToken,
  type IntentFilters,
  type IntentKey,
} from "@/lib/feedFilter";
import { NEAR_ME_KM } from "@/lib/nlSearch";

export interface FeedFiltersProps {
  query: string;
  onQueryChange: (q: string) => void;
  /** Top-N most-used taxonomy items to surface as quick chips. */
  recommended: ChipOption[];
  active: FilterToken[];
  onToggle: (token: FilterToken) => void;
  onClear: () => void;
  onOpenCustom: () => void;
  resultCount: number;
  /** Non-taxonomy dimensions in play (open now, capacity, distance, offers…). */
  intent: IntentFilters;
  onIntentRemove: (key: IntentKey) => void;
  /** The query reads like a sentence — offer the "Ask Resto" pill. */
  askable: boolean;
  /** The last ask understood nothing. */
  askMissed: boolean;
  /** An ask is waiting on the server upgrade (the offline parse is instant). */
  askPending: boolean;
  /** The current chips came from the AI model — label them as such. */
  askByAi?: boolean;
  onAsk: () => void;
}

/**
 * Consumer feed controls: a broad search bar, the "Ask Resto" pill that turns a
 * sentence into filters, a "Popular" quick-chip row (the most-used
 * cuisines/tags), the active filters as removable chips, and a button that
 * opens the full custom-filter modal. All client-side over the loaded feed.
 */
export function FeedFilters({
  query,
  onQueryChange,
  recommended,
  active,
  onToggle,
  onClear,
  onOpenCustom,
  resultCount,
  intent,
  onIntentRemove,
  askable,
  askMissed,
  askPending,
  askByAi = false,
  onAsk,
}: FeedFiltersProps) {
  const { t, lang } = useTranslation();
  const { cuisines, tags } = useTaxonomy();
  const reduceMotion = useReducedMotion();

  // Resolve a localized label for an active token (which only carries kind+slug).
  const labelOf = (tok: FilterToken): string => {
    const row =
      tok.kind === "cuisine"
        ? cuisines.find((c) => c.slug === tok.slug)
        : tags.find((tg) => tg.slug === tok.slug);
    return row ? labelFor(row, lang) : tok.slug;
  };

  // Intent chips read like the taxonomy ones, so the whole row stays one
  // vocabulary the visitor can edit. Capacity carries two labels (its level is
  // the value), distance carries its radius.
  const intentLabel = (key: IntentKey): string => {
    switch (key) {
      case "openNow":
        return t("HOME_FILTER_OPEN_NOW");
      case "hasDiscount":
        return t("HOME_FILTER_DISCOUNT");
      case "fastVenue":
        return t("HOME_FILTER_QUICK");
      case "capacityMax":
        return t(intent.capacityMax === "YELLOW" ? "HOME_FILTER_SOME_SPACE" : "HOME_FILTER_QUIET");
      case "maxDistanceKm":
        return t("HOME_FILTER_NEARBY", { km: intent.maxDistanceKm ?? NEAR_ME_KM });
    }
  };
  const intentChips = INTENT_KEYS.filter((key) => intent[key] !== undefined);
  const showChipRow = active.length > 0 || intentChips.length > 0;

  return (
    <div className="mt-5 space-y-3 sm:mt-6">
      {/* Search + custom-filter button */}
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-dim light:text-zinc-600"
            aria-hidden
          />
          <input
            type="search"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            onKeyDown={(e) => {
              // Enter on a sentence-like query does what the pill does — the
              // reflex on a phone keyboard's "search" key.
              if (e.key === "Enter" && askable) {
                e.preventDefault();
                onAsk();
              }
            }}
            placeholder={t("HOME_SEARCH_PLACEHOLDER")}
            aria-label={t("HOME_SEARCH_PLACEHOLDER")}
            className="w-full rounded-xl border border-white/[0.12] light:border-black/10 bg-zinc-900/70 light:bg-white/70 py-2.5 pl-9 pr-9 text-base text-zinc-100 light:text-zinc-900 shadow-[0_1px_0_rgba(255,255,255,0.05)_inset] outline-none transition placeholder:text-zinc-500 focus:border-white/30 light:focus:border-black/40 sm:text-sm"
          />
          <AnimatePresence>
            {query && (
              <motion.button
                type="button"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.12 }}
                onClick={() => onQueryChange("")}
                aria-label={t("HOME_SEARCH_CLEAR")}
                className="absolute right-2 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-full text-dim light:text-zinc-600 transition hover:bg-zinc-800 light:hover:bg-zinc-100 hover:text-zinc-200 light:hover:text-zinc-800"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </motion.button>
            )}
          </AnimatePresence>
        </div>

        <button
          type="button"
          onClick={onOpenCustom}
          aria-label={t("HOME_FILTERS_TITLE")}
          className={cn(
            "relative inline-flex shrink-0 items-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-medium transition",
            active.length > 0
              ? "border-white/30 light:border-black/25 bg-zinc-100 light:bg-zinc-900 text-zinc-900 light:text-zinc-50"
              : "border-white/[0.12] light:border-black/10 bg-zinc-900/70 light:bg-white/70 text-zinc-200 light:text-zinc-800 hover:border-white/30 light:hover:border-black/30",
          )}
        >
          <SlidersHorizontal className="h-4 w-4" aria-hidden />
          <span className="hidden sm:inline">{t("HOME_FILTERS_CUSTOM")}</span>
          {active.length > 0 && (
            <span
              className={cn(
                "grid h-4 min-w-4 place-items-center rounded-full px-1 text-3xs font-bold tabular-nums",
                "bg-pulse text-white",
              )}
            >
              {active.length}
            </span>
          )}
        </button>
      </div>

      {/* "Ask Resto" — appears once the query reads like a sentence; tapping it
          turns the sentence into the chips below, which stay fully editable. */}
      <AnimatePresence initial={false}>
        {askable && (
          // Height-collapsing tween, like the active-filters row below: a spring
          // on opacity alone leaves the (invisible) row holding its space in the
          // flow for a beat after it fades, which reads as a layout hiccup.
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={reduceMotion ? { duration: 0 } : { duration: 0.2, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <Button
                onClick={onAsk}
                disabled={askPending}
                aria-busy={askPending}
                className="rounded-full px-3 py-1.5 text-xs"
              >
                {/* Spinner only while the server upgrade is answering — the
                    offline parse resolves before this could ever render. */}
                {askPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                ) : (
                  <Sparkles className="h-3.5 w-3.5" aria-hidden />
                )}
                {t("AI_ASK_PILL")}
              </Button>
              <span className="text-xs text-dim light:text-zinc-600">
                {askPending
                  ? t("AI_ASK_PENDING")
                  : askMissed
                    ? t("AI_ASK_MISSED")
                    : t("AI_ASK_HINT")}
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Popular quick-chips */}
      {recommended.length > 0 && (
        <div>
          <p className="mb-1.5 font-mono text-3xs uppercase tracking-[0.2em] text-dim light:text-zinc-600">
            {t("HOME_FILTERS_RECOMMENDED")}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {recommended.map((opt) => {
              const token: FilterToken = { kind: opt.kind, slug: opt.slug };
              const on = hasToken(active, token);
              const Icon = opt.kind === "cuisine" ? Utensils : Tag;
              return (
                <button
                  key={`${opt.kind}:${opt.slug}`}
                  type="button"
                  onClick={() => onToggle(token)}
                  aria-pressed={on}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition sm:py-1",
                    on
                      ? "border-white/30 light:border-black/25 bg-zinc-100 light:bg-zinc-900 text-zinc-900 light:text-zinc-50"
                      : "border-white/[0.12] light:border-black/10 bg-zinc-900/60 light:bg-white/70 text-zinc-300 light:text-zinc-700 hover:border-white/30 light:hover:border-black/30 hover:text-zinc-100 light:hover:text-zinc-900",
                  )}
                >
                  <Icon className={cn("h-3 w-3", on ? "text-dim light:text-zinc-600" : "text-dim light:text-zinc-600")} aria-hidden />
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Active filters */}
      <AnimatePresence initial={false}>
        {showChipRow && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={reduceMotion ? { duration: 0 } : { duration: 0.2, ease: "easeOut" }}
            className="overflow-hidden"
          >
            {askByAi && (
              <p className="mb-1.5 inline-flex items-center gap-1 font-mono text-3xs uppercase tracking-[0.2em] text-dim light:text-zinc-600">
                <Sparkles className="h-3 w-3" aria-hidden />
                {t("AI_ASK_SUGGESTED")}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-1.5">
              <AnimatePresence initial={false}>
                {active.map((tok) => (
                  <motion.button
                    key={`${tok.kind}:${tok.slug}`}
                    type="button"
                    layout
                    initial={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.85 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.85 }}
                    transition={reduceMotion ? { duration: 0 } : SPRING_PILL}
                    onClick={() => onToggle(tok)}
                    className="inline-flex items-center gap-1 rounded-full border border-white/30 light:border-black/25 bg-zinc-100 light:bg-zinc-900 py-1.5 pl-3 pr-2 text-xs font-medium text-zinc-900 light:text-zinc-50 sm:py-1"
                  >
                    {labelOf(tok)}
                    <X className="h-3 w-3 text-dim light:text-zinc-600" aria-hidden />
                  </motion.button>
                ))}
                {intentChips.map((key) => (
                  <motion.button
                    key={`intent:${key}`}
                    type="button"
                    layout
                    initial={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.85 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.85 }}
                    transition={reduceMotion ? { duration: 0 } : SPRING_PILL}
                    onClick={() => onIntentRemove(key)}
                    className="inline-flex items-center gap-1 rounded-full border border-white/30 light:border-black/25 bg-zinc-100 light:bg-zinc-900 py-1.5 pl-3 pr-2 text-xs font-medium text-zinc-900 light:text-zinc-50 sm:py-1"
                  >
                    {intentLabel(key)}
                    <X className="h-3 w-3 text-dim light:text-zinc-600" aria-hidden />
                  </motion.button>
                ))}
              </AnimatePresence>
              <button
                type="button"
                onClick={onClear}
                className="ml-1 text-xs font-medium text-zinc-400 light:text-zinc-600 underline-offset-2 transition hover:text-zinc-100 light:hover:text-zinc-900 hover:underline"
              >
                {t("HOME_FILTERS_CLEAR")}
              </button>
              <span className="ml-auto font-mono text-3xs uppercase tracking-[0.16em] text-dim light:text-zinc-600">
                {resultCount === 1
                  ? t("HOME_RESULT_COUNT_ONE")
                  : t("HOME_RESULT_COUNT", { n: resultCount })}
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
